import {HttpClient, HttpHeaders, HttpParams} from '@angular/common/http';
import {Observable, shareReplay, throwError} from 'rxjs';
import {catchError, map, tap, timeout} from 'rxjs/operators';
import {CrystalclearBackUrl} from './config/crystalclear-back-url';
import {environment} from '../../environments/environment';
import UrlConf = CrystalclearBackUrl.UrlConf;

export abstract class ParentAPIService {
  protected http: HttpClient;

  protected constructor(http: HttpClient) {
    this.http = http;
  }

  /**
   * Returns a query string for GET requests corresponding to the given parameters
   * @param params An array of object, each object can contain <key - value> pairs
   */
  public static getQueryString(params?: {}): string {
    const paramArray = [];
    if (params) {
      for (const key of Object.keys(params)) {
        // @ts-ignore
        const value = params[key];
        if (typeof value !== 'undefined') {
          paramArray.push(key + '=' + encodeURIComponent(value));
        }
      }
    }
    return paramArray.length > 0 ? '?' + paramArray.join('&') : '';
  }

  private static fromJson<T>(relativeUrl: CrystalclearBackUrl.UrlConf<T>, value: object | any[]) {
    return value;
    // @ts-ignore
    // return TysonUtils.getInstance().fromJson(value, relativeUrl.type);
  }

  public getUrl(relativeUrl: string, queryStr: (string | number) = '') {
    return this.apiUrl() + relativeUrl + queryStr;
  }

  protected apiUrl(): string {
    return environment.apiUrl;
  }

  protected get<T>(relativeUrl: UrlConf<T>, params?: {}): Observable<T> {
    const url = this.getUrl(relativeUrl.url, ParentAPIService.getQueryString(params));
    // @ts-ignore
    return this.http.get(url)
      .pipe(
        map(value => {
          return ParentAPIService.fromJson(relativeUrl, value);
        })
      );
  }


  protected getArray<T>(relativeUrl: UrlConf<T>, params?: {}): Observable<T[]> {
    const url = this.getUrl(relativeUrl.url, ParentAPIService.getQueryString(params));
    // @ts-ignore
    return this.http.get<[]>(url)
      .pipe(
        map(value => {
          return ParentAPIService.fromJson(relativeUrl, value);
        })
      );
  }

  protected getResource(relativeUrl: string, params?: {}) {
    const url = this.getUrl(relativeUrl, ParentAPIService.getQueryString(params));
    return this.http.get(url, {
      headers: {'Content-Type': 'application/octet-stream'},
      observe: 'response',
      responseType: 'blob'
    });
  }

  protected postResource(relativeUrl: string, params?: {}) {
    const url = this.getUrl(relativeUrl, ParentAPIService.getQueryString(params));
    return this.http.post(url, {}, {
      headers: {'Content-Type': 'application/octet-stream'},
      observe: 'response',
      responseType: 'blob'
    });
  }

  protected putResource(relativeUrl: string, body?: any, params?: any) {
    const url = this.getUrl(relativeUrl, ParentAPIService.getQueryString(params));
    return this.http.put(url, body, {
      headers: {'Content-Type': 'application/json'},
      observe: 'response',
      responseType: 'blob'
    });
  }

  protected post<T>(relativeUrl: UrlConf<T>, body?: any, pageParameters?: any): Observable<T> {
    const url = this.getUrl(relativeUrl.url, ParentAPIService.getQueryString(pageParameters));
    // @ts-ignore
    return this.http.post(url, body)
      .pipe(timeout(600000)).pipe(map(value => {
          return ParentAPIService.fromJson(relativeUrl, value);
        }),
        catchError((err: any) => {
          return throwError(err);
        })
      );
  }

  protected postStore<T>(relativeUrl: UrlConf<T>, body?: any, pageParameters?: any): Observable<T> {
    const storageKey: string = relativeUrl.url.replace('\\', '_');
    const resultItemSt: string = localStorage.getItem(storageKey);
    if (!resultItemSt) {
      const url = this.getUrl(relativeUrl.url, ParentAPIService.getQueryString(pageParameters));
      return this.http.post(url, body)
        .pipe(timeout(600000)).pipe(map(value => {
            const resultItem: any = ParentAPIService.fromJson(relativeUrl, value);
            localStorage.setItem(storageKey, JSON.stringify(resultItem));
            return resultItem;
          }),
          catchError((err: any) => {
            return throwError(err);
          })
        );
    } else {
      return new Observable(subscriber => {
        subscriber.next(JSON.parse(resultItemSt));
        subscriber.complete();
      });
    }
  }

  protected getStore<T>(relativeUrl: UrlConf<T>, body?: any, pageParameters?: any): Observable<T> {
    const storageKey: string = relativeUrl.url.replace('\\', '_');
    const resultItemSt: string = localStorage.getItem(storageKey);
    if (!resultItemSt) {
      const url = this.getUrl(relativeUrl.url, ParentAPIService.getQueryString(pageParameters));
      return this.http.get(url, body)
        .pipe(timeout(600000)).pipe(map(value => {
            const resultItem: any = ParentAPIService.fromJson(relativeUrl, value);
            localStorage.setItem(storageKey, JSON.stringify(resultItem));
            return resultItem;
          }),
          catchError((err: any) => {
            return throwError(err);
          })
        );
    } else {
      return new Observable(subscriber => {
        subscriber.next(JSON.parse(resultItemSt));
        subscriber.complete();
      });
    }
  }

  protected clearStore<T>(relativeUrl: UrlConf<T>, body?: any): void {
    const storageKey: string = relativeUrl.url.replace('\\', '_');
    localStorage.removeItem(storageKey);
  }

  protected isInStore<T>(relativeUrl: UrlConf<T>, body?: any): boolean {
    const storageKey: string = relativeUrl.url.replace('\\', '_');
    return localStorage.getItem(storageKey)?true:false;
  }

  protected postFormData<T>(relativeUrl: UrlConf<T>, formData?: { [param: string]: string | string[] },
                            pageParameters?: any): Observable<T> {
    const url = this.getUrl(relativeUrl.url, ParentAPIService.getQueryString(pageParameters));
    const options = {
      headers: new HttpHeaders()
        .set('Content-Type', 'application/x-www-form-urlencoded')
    };

    const httpParams = new HttpParams({
      fromObject: formData
    });

    // @ts-ignore
    return this.http.post(url, httpParams, options)
      .pipe(map(value => ParentAPIService.fromJson(relativeUrl, value)));
  }

  protected delete<T>(relativeUrl: UrlConf<T>, pageParameters?: {}) {
    const url = this.getUrl(relativeUrl.url, ParentAPIService.getQueryString(pageParameters));
    return this.http.delete(url);
  }

  protected put<T>(relativeUrl: UrlConf<T>, body?: any, pageParameters?: any): Observable<T> {
    const url = this.getUrl(relativeUrl.url, ParentAPIService.getQueryString(pageParameters));
    // @ts-ignore
    return this.http.put(url, body)
      .pipe(map(value => ParentAPIService.fromJson(relativeUrl, value)));
  }
}
